import React, { useEffect, useRef } from "react";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { Alert, Grid, InputLabel, MenuItem, OutlinedInput, Select } from "@mui/material";
import { useContratosState } from "../context/contratos-context";
import { useContratosController } from "../context/contratos-controller";
import PlainTextArea from "../../../components/plain-text-area";
import { SelectWithQuickAdd } from "../../../components/select-with-quick-add";
import { TextInputWithCounter } from "../../../components/text-input-with-counter";
import { CurrencyInput } from "../../../components/currency-input";
import AdminPortalAppService from "../../../../application/platform/admin-portal/admin-portal-app-service";
import { ContratoDocumentosSection } from "./contrato-documentos-section";

export const ContratoForm = () => {
    const { dataRequest, tiposList, situacoesList, licitacoesList, fornecedoresList } = useContratosState();
    const { handleOnChangeForm, loadLookups } = useContratosController();
    const firstInputRef = useRef<HTMLInputElement>(null);
    const isExistingRecord = dataRequest?.id !== undefined && dataRequest?.id !== null;

    useEffect(() => {
        if (firstInputRef.current) {
            firstInputRef.current.focus();
        }
        (async () => {
            await loadLookups();
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <React.Fragment>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Grid container spacing={2}>
                    <Grid item lg={12} sm={12} xl={12}>
                        <Alert severity="info">
                            Se Licitação ou Fornecedor ainda não existirem, cadastre-os primeiro no menu lateral. Tipo de Contrato pode ser adicionado na hora pelo botão "+" ao lado do campo.
                        </Alert>
                    </Grid>

                    <Grid item lg={6} sm={6} xl={6}>
                        <InputLabel>Licitação *</InputLabel>
                        <Select
                            size="small"
                            error={!dataRequest?.idLicitacao}
                            value={dataRequest?.idLicitacao ?? ''}
                            onChange={(e) => handleOnChangeForm('idLicitacao', e.target.value)}
                            fullWidth
                        >
                            <MenuItem value={''} key={0}><em>Selecionar</em></MenuItem>
                            {licitacoesList?.slice().sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')).map((o) => <MenuItem value={o.id} key={o.id}>{o.name}</MenuItem>)}
                        </Select>
                    </Grid>

                    <Grid item lg={6} sm={6} xl={6}>
                        <InputLabel>Fornecedor *</InputLabel>
                        <Select
                            size="small"
                            error={!dataRequest?.idFornecedor}
                            value={dataRequest?.idFornecedor ?? ''}
                            onChange={(e) => handleOnChangeForm('idFornecedor', e.target.value)}
                            fullWidth
                        >
                            <MenuItem value={''} key={0}><em>Selecionar</em></MenuItem>
                            {fornecedoresList?.slice().sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')).map((o) => <MenuItem value={o.id} key={o.id}>{o.name}</MenuItem>)}
                        </Select>
                    </Grid>

                    <Grid item lg={4} sm={4} xl={4}>
                        <InputLabel>Número do Contrato *</InputLabel>
                        <TextInputWithCounter
                            ref={firstInputRef}
                            size="small"
                            maxLength={20}
                            error={!dataRequest?.numContrato}
                            value={dataRequest?.numContrato || ''}
                            onChange={(e) => handleOnChangeForm('numContrato', e.target.value)}
                            fullWidth
                        />
                    </Grid>

                    <Grid item lg={4} sm={4} xl={4}>
                        <SelectWithQuickAdd
                            label="Tipo de Contrato *"
                            error={!dataRequest?.idTipoContrato}
                            value={dataRequest?.idTipoContrato ?? ''}
                            onChange={(value) => handleOnChangeForm('idTipoContrato', value)}
                            options={tiposList}
                            addLabel="Novo Tipo de Contrato"
                            onAdd={async (nome) => {
                                await AdminPortalAppService.addUpdateTipoContrato({ nomTipoContrato: nome });
                                await loadLookups();
                            }}
                        />
                    </Grid>

                    <Grid item lg={4} sm={4} xl={4}>
                        <InputLabel>Situação *</InputLabel>
                        <Select
                            size="small"
                            error={!dataRequest?.idSituacaoContrato}
                            value={dataRequest?.idSituacaoContrato ?? ''}
                            onChange={(e) => handleOnChangeForm('idSituacaoContrato', e.target.value)}
                            fullWidth
                        >
                            <MenuItem value={''} key={0}><em>Selecionar</em></MenuItem>
                            {situacoesList?.slice().sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')).map((o) => <MenuItem value={o.id} key={o.id}>{o.name}</MenuItem>)}
                        </Select>
                    </Grid>

                    <Grid item lg={6} sm={6} xl={6}>
                        <InputLabel>Nº Processo Administrativo</InputLabel>
                        <TextInputWithCounter
                            size="small"
                            maxLength={20}
                            value={dataRequest?.numProcessoAdministrativo || ''}
                            onChange={(e) => handleOnChangeForm('numProcessoAdministrativo', e.target.value)}
                            fullWidth
                        />
                    </Grid>

                    <Grid item lg={6} sm={6} xl={6}>
                        <InputLabel>Valor do Contrato *</InputLabel>
                        <CurrencyInput
                            size="small"
                            error={!dataRequest?.valorContrato || dataRequest.valorContrato <= 0}
                            value={dataRequest?.valorContrato}
                            onChange={(value) => handleOnChangeForm('valorContrato', value)}
                            fullWidth
                        />
                    </Grid>

                    <Grid item lg={12} sm={12} xl={12}>
                        <InputLabel>Objeto *</InputLabel>
                        <PlainTextArea
                            size="small"
                            rows={4}
                            error={!dataRequest?.textoObjetoContrato}
                            value={dataRequest?.textoObjetoContrato || ''}
                            onChange={(e: any) => handleOnChangeForm('textoObjetoContrato', e.target.value)}
                            fullWidth
                        />
                    </Grid>

                    <Grid item lg={3} sm={3} xl={3}>
                        <InputLabel>Data de Assinatura *</InputLabel>
                        <DesktopDatePicker
                            format='DD/MM/YYYY'
                            slotProps={{ textField: { fullWidth: true, size: "small", error: !dataRequest?.dtaAssinatura } }}
                            value={dataRequest?.dtaAssinatura ? dayjs(dataRequest.dtaAssinatura) : null}
                            onChange={(value: Dayjs | null) => handleOnChangeForm('dtaAssinatura', value?.toISOString())}
                        />
                    </Grid>

                    <Grid item lg={3} sm={3} xl={3}>
                        <InputLabel>Início da Vigência *</InputLabel>
                        <DesktopDatePicker
                            format='DD/MM/YYYY'
                            slotProps={{ textField: { fullWidth: true, size: "small", error: !dataRequest?.dtaInicioVigencia } }}
                            value={dataRequest?.dtaInicioVigencia ? dayjs(dataRequest.dtaInicioVigencia) : null}
                            onChange={(value: Dayjs | null) => handleOnChangeForm('dtaInicioVigencia', value?.toISOString())}
                        />
                    </Grid>

                    <Grid item lg={3} sm={3} xl={3}>
                        <InputLabel>Fim da Vigência *</InputLabel>
                        <DesktopDatePicker
                            format='DD/MM/YYYY'
                            slotProps={{ textField: { fullWidth: true, size: "small", error: !dataRequest?.dtaFimVigencia } }}
                            value={dataRequest?.dtaFimVigencia ? dayjs(dataRequest.dtaFimVigencia) : null}
                            onChange={(value: Dayjs | null) => handleOnChangeForm('dtaFimVigencia', value?.toISOString())}
                        />
                    </Grid>

                    <Grid item lg={3} sm={3} xl={3}>
                        <InputLabel>Data de Publicação *</InputLabel>
                        <DesktopDatePicker
                            format='DD/MM/YYYY'
                            slotProps={{ textField: { fullWidth: true, size: "small", error: !dataRequest?.dtaPublicacao } }}
                            value={dataRequest?.dtaPublicacao ? dayjs(dataRequest.dtaPublicacao) : null}
                            onChange={(value: Dayjs | null) => handleOnChangeForm('dtaPublicacao', value?.toISOString())}
                        />
                    </Grid>

                    <Grid item lg={12} sm={12} xl={12}>
                        <InputLabel>PNCP Url</InputLabel>
                        <OutlinedInput
                            size="small"
                            value={dataRequest?.pncpUrl || ''}
                            onChange={(e) => handleOnChangeForm('pncpUrl', e.target.value)}
                            fullWidth
                        />
                    </Grid>

                    {isExistingRecord ? (
                        <ContratoDocumentosSection />
                    ) : (
                        <Grid item lg={12} sm={12} xl={12}>
                            <Alert severity="info">
                                Salve o contrato primeiro para liberar o cadastro de Documentos.
                            </Alert>
                        </Grid>
                    )}
                </Grid>
            </LocalizationProvider>
        </React.Fragment>
    );
};
